/**
 * Capability tiering API (cdb_tqs#700) — tqs routes /api/v1/capabilities/*.
 *
 * Keys are code-owned in the tqs registry; tiers/assignments/overrides are
 * config edited in the Cortex "Communicator" hub. All admin routes require
 * the admin role; ``getMyCapabilities`` is the caller-facing endpoint the
 * Communicator frontends use for feature gating.
 *
 * Note the proxy shape: the BFF catch-all maps /api/v1/tqs/<path> onto the
 * tqs backend's /api/v1/<path>, so these calls go through
 * ``${CDB_TQS_API_BASE_URL}/capabilities/...`` like every other tqs domain.
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

export type CapabilityValueType = "bool" | "enum" | "list" | "number";

export type CapabilityValue = boolean | string | number | string[];

export interface CapabilityDef {
  key: string;
  type: CapabilityValueType;
  group: string;
  description: string;
  /** Value used when an assigned tier does not mention the key. */
  unset_default: CapabilityValue;
  /** Value for clients without any tier assignment. */
  minimal: CapabilityValue;
  enum_values: string[] | null;
  list_catalog: string[] | null;
  /** Skills whose access is gated by this key (badge in the Skills tab). */
  skill_names: string[];
  /**
   * Whether this capability may be gated per role (cdb_tqs#790). Product-core
   * capabilities have ``role_gateable=false`` and must not appear in the role
   * restriction UI. Defaults true for older registries without the field.
   */
  role_gateable?: boolean;
}

export interface CapabilityRegistry {
  capabilities: CapabilityDef[];
  products: string[];
  model_catalog: string[];
}

export interface CapabilityTier {
  id: string;
  name: string;
  description: string | null;
  product: string | null;
  is_system: boolean;
  capabilities: Record<string, CapabilityValue>;
  assigned_clients: number;
}

export interface TierCreatePayload {
  name: string;
  description?: string | null;
  product?: string | null;
  capabilities: Record<string, CapabilityValue>;
}

export interface TierUpdatePayload {
  name?: string;
  description?: string | null;
  product?: string | null;
  capabilities?: Record<string, CapabilityValue>;
}

export type AssignmentMode = "explicit" | "inherit";

export interface ClientProductAssignment {
  mode: AssignmentMode;
  tier_id: string | null;
  tier_name: string | null;
}

export interface ClientAssignments {
  client_id: string;
  client_name: string;
  /** Keyed by product ('cdb' | 'pdb'); missing key = not booked. */
  assignments: Record<string, ClientProductAssignment>;
}

export interface AssignmentPutPayload {
  product: string;
  mode: AssignmentMode;
  tier_id?: string | null;
}

export interface CapabilityOverride {
  capability_key: string;
  value: CapabilityValue;
  updated_at: string;
}

export interface MyCapabilities {
  product: string;
  capabilities: Record<string, CapabilityValue>;
}

export interface ResolvedClientCapabilities extends MyCapabilities {
  client_id: string;
}

/** #713 §2 — per-client role narrowing: skill × allowed roles WITHIN the client. */
export interface SkillRoleRestriction {
  skill_id: string;
  skill_name: string;
  allowed_role_ids: string[];
  updated_at: string;
}

const BASE = `${CDB_TQS_API_BASE_URL}/capabilities`;

export const capabilitiesApi = {
  async getCapabilityRegistry(): Promise<CapabilityRegistry> {
    const response = await fetchWithLogging(`${BASE}/registry`, { method: "GET" });
    return response.data as CapabilityRegistry;
  },

  async listCapabilityTiers(): Promise<CapabilityTier[]> {
    const response = await fetchWithLogging(`${BASE}/tiers`, { method: "GET" });
    return response.data as CapabilityTier[];
  },

  async createCapabilityTier(data: TierCreatePayload): Promise<{ id: string }> {
    const response = await fetchWithLogging(`${BASE}/tiers`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.data as { id: string };
  },

  async updateCapabilityTier(tierId: string, data: TierUpdatePayload): Promise<void> {
    await fetchWithLogging(`${BASE}/tiers/${tierId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  async deleteCapabilityTier(tierId: string): Promise<void> {
    await fetchWithLogging(`${BASE}/tiers/${tierId}`, { method: "DELETE" });
  },

  async listCapabilityAssignments(): Promise<ClientAssignments[]> {
    const response = await fetchWithLogging(`${BASE}/assignments`, { method: "GET" });
    return response.data as ClientAssignments[];
  },

  async putCapabilityAssignment(
    clientId: string,
    data: AssignmentPutPayload,
  ): Promise<void> {
    await fetchWithLogging(`${BASE}/assignments/${clientId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  async deleteCapabilityAssignment(clientId: string, product: string): Promise<void> {
    await fetchWithLogging(`${BASE}/assignments/${clientId}/${product}`, {
      method: "DELETE",
    });
  },

  async listCapabilityOverrides(clientId: string): Promise<CapabilityOverride[]> {
    const response = await fetchWithLogging(`${BASE}/overrides/${clientId}`, {
      method: "GET",
    });
    return response.data as CapabilityOverride[];
  },

  async putCapabilityOverride(
    clientId: string,
    capabilityKey: string,
    value: CapabilityValue,
  ): Promise<void> {
    await fetchWithLogging(`${BASE}/overrides/${clientId}`, {
      method: "PUT",
      body: JSON.stringify({ capability_key: capabilityKey, value }),
    });
  },

  async deleteCapabilityOverride(clientId: string, capabilityKey: string): Promise<void> {
    await fetchWithLogging(`${BASE}/overrides/${clientId}/${capabilityKey}`, {
      method: "DELETE",
    });
  },

  async listSkillRoleRestrictions(clientId: string): Promise<SkillRoleRestriction[]> {
    const response = await fetchWithLogging(`${BASE}/restrictions/${clientId}`, {
      method: "GET",
    });
    return response.data as SkillRoleRestriction[];
  },

  async putSkillRoleRestriction(
    clientId: string,
    skillId: string,
    allowedRoleIds: string[],
  ): Promise<void> {
    await fetchWithLogging(`${BASE}/restrictions/${clientId}`, {
      method: "PUT",
      body: JSON.stringify({ skill_id: skillId, allowed_role_ids: allowedRoleIds }),
    });
  },

  async deleteSkillRoleRestriction(clientId: string, skillId: string): Promise<void> {
    await fetchWithLogging(`${BASE}/restrictions/${clientId}/${skillId}`, {
      method: "DELETE",
    });
  },

  async getMyCapabilities(product: string = "cdb"): Promise<MyCapabilities> {
    const response = await fetchWithLogging(`${BASE}/me`, {
      method: "GET",
      params: { product },
    });
    return response.data as MyCapabilities;
  },

  async getResolvedClientCapabilities(
    clientId: string,
    product: string = "cdb",
  ): Promise<ResolvedClientCapabilities> {
    const response = await fetchWithLogging(`${BASE}/resolved/${clientId}`, {
      method: "GET",
      params: { product },
    });
    return response.data as ResolvedClientCapabilities;
  },
};
